import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    CheckCircle2,
    XCircle,
    BookOpen,
    AlertTriangle,
    Loader2,
    ChevronLeft,
    ChevronRight,
    Menu,
    Circle,
    ClipboardCheck,
    X,
    Play,
    Expand,
    Shrink,
} from 'lucide-react';
import { useModal } from '../../../../hooks/useModal';
import { ApiService } from '../../../../services/api';
import SolutionVideoModal from '../homework/SolutionVideoModal';
import type {
    ErrorBookletWithQuestions,
    ErrorBookletQuestion,
    ErrorBookletCompletionResult,
} from '../../../../types/error-booklet';

interface ErrorBookletTestProps {
    bookletId: string;
    onBack?: () => void;
}

const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

export default function ErrorBookletTest({ bookletId, onBack }: ErrorBookletTestProps) {
    const navigate = useNavigate();
    const { showAlert, showConfirm } = useModal();

    const [booklet, setBooklet] = useState<ErrorBookletWithQuestions | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, any>>({});
    const [result, setResult] = useState<ErrorBookletCompletionResult | null>(null);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const imageContainerRef = useRef<HTMLDivElement>(null);
    const [imageZoom, setImageZoom] = useState(1);
    const [isSolutionVideoModalOpen, setIsSolutionVideoModalOpen] = useState(false);
    const [selectedQuestionForSolution, setSelectedQuestionForSolution] = useState<any>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const localStorageKey = `error_booklet_answers_${bookletId}`;

    useEffect(() => {
        const savedAnswers = localStorage.getItem(localStorageKey);
        if (savedAnswers) {
            try {
                const parsed = JSON.parse(savedAnswers);
                setAnswers(parsed);
            } catch (e) {
                console.error('Error parsing saved answers:', e);
            }
        };
    }, [localStorageKey]);

    useEffect(() => {
        if (Object.keys(answers).length > 0 && !isCompleted) localStorage.setItem(localStorageKey, JSON.stringify(answers));
    }, [answers, localStorageKey, isCompleted]);

    useEffect(() => {
        if (bookletId) loadBooklet();
    }, [bookletId]);

    useEffect(() => {
        const onFsChange = () => {
            const isNowFullscreen = !!document.fullscreenElement;
            setIsFullscreen(isNowFullscreen);
        };
        document.addEventListener('fullscreenchange', onFsChange);
        return () => document.removeEventListener('fullscreenchange', onFsChange);
    }, []);

    const handleExpand = () => {
        const container = document.getElementById('error-booklet-test-container');
        if (container) {
            if (isFullscreen) {
                document.exitFullscreen();
                setIsFullscreen(false);
            } else {
                container.requestFullscreen();
                setIsFullscreen(true);
            }
        }
    };

    const loadBooklet = async () => {
        try {
            setLoading(true);
            const response = await ApiService.getErrorBookletDetails(bookletId);
            setBooklet(response);

            if (response.questions) {
                const serverAnswers: Record<string, any> = {};
                response.questions.forEach((q: ErrorBookletQuestion) => {
                    if (q.answer !== null) serverAnswers[q.id] = q.answer;
                });

                const savedAnswers = localStorage.getItem(localStorageKey);
                let localAnswers: Record<string, any> = {};
                if (savedAnswers) {
                    try {
                        localAnswers = JSON.parse(savedAnswers);
                    } catch (e) {
                        console.error('Error parsing saved answers:', e);
                    }
                };

                if (response.status === 'COMPLETED') {
                    setAnswers(serverAnswers);
                    setIsCompleted(true);

                    localStorage.removeItem(localStorageKey);
                } else {
                    const mergedAnswers = { ...serverAnswers, ...localAnswers };

                    setAnswers(mergedAnswers);
                };
            };
        } catch (error: any) {
            console.error('Error loading booklet:', error);
            await showAlert({
                title: 'Hata',
                message: error.message || 'Hata kitapçığı yüklenirken bir sorun oluştu.',
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const questions = booklet?.questions || [];
    const currentQuestion = questions[currentQuestionIndex];

    const answeredCount = useMemo(() => {
        return Object.keys(answers).length;
    }, [answers]);

    const handleAnswerSelect = (questionId: string, option: any) => {
        if (isCompleted) return;
        setAnswers(prev => {
            if (prev[questionId] === option) {
                const newAnswers = { ...prev };
                delete newAnswers[questionId];
                return newAnswers;
            }
            return { ...prev, [questionId]: option };
        });
    };

    const goToQuestion = (index: number) => {
        if (index >= 0 && index < questions.length) {
            setCurrentQuestionIndex(index);
            setImageZoom(1);
        }
    };

    const goToPreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
            setImageZoom(1);
        }
    };

    const goToNextQuestion = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
            setImageZoom(1);
        }
    };

    const handleSubmit = async () => {
        if (answeredCount === 0) {
            await showAlert({
                title: 'Hiç Soru Cevaplanmamış',
                message: 'Testi tamamlamak için en az bir soruyu cevaplamanız gerekiyor.',
                type: 'warning',
            });
            return;
        }

        if (answeredCount < questions.length) {
            const confirm = await showConfirm({
                title: 'Eksik Cevaplar',
                message: `${questions.length - answeredCount} soru henüz cevaplanmadı. Yine de testi tamamlamak istiyor musunuz?`,
                confirmText: 'Tamamla',
                cancelText: 'Devam Et',
            });
            if (!confirm) return;
        }

        try {
            setSubmitting(true);
            const formattedAnswers = Object.entries(answers).map(([questionId, answer]) => ({
                questionId,
                answer,
            }));

            const response = await ApiService.completeErrorBooklet(bookletId, formattedAnswers);
            setResult(response);
            setIsCompleted(true);

            localStorage.removeItem(localStorageKey);

            await showAlert({
                title: 'Tebrikler!',
                message: `Testi tamamladın. Başarı oranın: %${response.score}`,
                type: response.score >= 60 ? 'success' : 'warning',
            });
        } catch (error: any) {
            await showAlert({
                title: 'Hata',
                message: error.message || 'Test tamamlanırken bir sorun oluştu.',
                type: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate('/lessons?tab=error-booklet');
        }
    };

    const calculateExamStats = () => {
        if (!isCompleted || !booklet) {
            return { totalQuestions: 0, correctAnswers: 0, wrongAnswers: 0, unanswered: 0, successRate: 0 };
        }

        const resultData = result || {
            totalQuestions: booklet.totalQuestions,
            correctAnswers: booklet.correctAnswers,
            incorrectAnswers: booklet.totalQuestions - booklet.correctAnswers - (booklet.totalQuestions - booklet.solvedQuestions),
        };

        const totalQuestions = resultData.totalQuestions;
        const correctAnswers = resultData.correctAnswers;
        const wrongAnswers = resultData.incorrectAnswers || (totalQuestions - correctAnswers);
        const unanswered = totalQuestions - answeredCount;
        const successRate = Math.round((correctAnswers / totalQuestions) * 100);

        return { totalQuestions, correctAnswers, wrongAnswers, unanswered, successRate };
    };

    const getQuestionCardClass = (index: number) => {
        const baseClass = "p-1.5 rounded-md border-2 cursor-pointer transition-all text-sm";
        const isActive = index === currentQuestionIndex;

        if (!isCompleted) {
            const isAnswered = answers[questions[index].id];
            if (isAnswered) {
                return `${baseClass} border-blue-400 bg-blue-50 ${isActive ? "border-blue-500 bg-blue-100" : ""}`;
            }
            return `${baseClass} ${isActive ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50"
                }`;
        }

        const question = questions[index];
        const userAnswer = answers[question.id];
        const correctAnswer = question.originalQuestion?.correctOption;

        if (!userAnswer) {
            return `${baseClass} border-amber-400 bg-amber-50 ${isActive ? "border-amber-500 bg-amber-100" : ""}`;
        }
        if (JSON.stringify(userAnswer) === JSON.stringify(correctAnswer)) {
            return `${baseClass} border-green-400 bg-green-50 ${isActive ? "border-green-500 bg-green-100" : ""}`;
        }
        return `${baseClass} border-red-400 bg-red-50 ${isActive ? "border-red-500 bg-red-100" : ""}`;
    };

    const getAnswerStatus = (questionId: string) => {
        if (!isCompleted) return null;
        const question = questions.find(q => q.id === questionId);
        const userAnswer = answers[questionId];
        const correctAnswer = question?.originalQuestion?.correctOption;

        if (!userAnswer) return "empty";
        if (JSON.stringify(userAnswer) === JSON.stringify(correctAnswer)) return "correct";
        return "incorrect";
    };

    const handleImageClick = () => {
        setImageZoom(prev => prev === 1 ? 2 : 1);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] bg-slate-50 rounded-xl p-8">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                </div>
                <p className="mt-6 text-lg font-semibold text-slate-700">Test yükleniyor...</p>
            </div>
        );
    }

    if (!booklet) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] bg-slate-50 rounded-xl p-8">
                <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
                <p className="text-lg font-semibold text-slate-700 mb-2">Hata Kitapçığı Bulunamadı</p>
                <p className="text-slate-500 mb-4">İstenen kitapçık mevcut değil veya silinmiş olabilir.</p>
                <button
                    onClick={handleBack}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Geri Dön
                </button>
            </div>
        );
    }

    const stats = calculateExamStats();

    return (
        <>
            <div id="error-booklet-test-container" className={`w-full bg-gray-100 flex flex-col h-screen ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
                <div className="flex flex-1 flex-col md:flex-row min-h-0">
                    <div className={`${isSidebarCollapsed ? "w-12" : "w-full md:w-64 lg:w-56"
                        } bg-white border-r md:border-r border-b md:border-b-0 border-slate-200 flex flex-col transition-all duration-300 min-h-0 flex-shrink-0 md:flex-shrink`}>
                        <div className={`h-16 border-b border-slate-200 flex items-center ${isSidebarCollapsed ? 'justify-center px-0' : 'px-3 sm:px-4 gap-2'}`}>
                            {!isSidebarCollapsed && (
                                <button
                                    onClick={handleBack}
                                    className="p-2 hover:bg-slate-50 rounded-md transition-colors"
                                    title="Geri Dön"
                                >
                                    <ArrowLeft className="w-5 h-5 text-slate-600" />
                                </button>
                            )}
                            {!isSidebarCollapsed && (
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-semibold text-slate-800 truncate">
                                        {monthNames[booklet.month - 1]} {booklet.year}
                                    </h3>
                                    <p className="text-xs text-slate-500">Hata Kitapçığı</p>
                                </div>
                            )}
                            <button
                                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                                className="p-2 hover:bg-slate-50 rounded-md transition-colors"
                                title={isSidebarCollapsed ? "Paneli Aç" : "Paneli Kapat"}
                            >
                                {isSidebarCollapsed ? <Menu className="w-5 h-5 text-slate-600" /> : <ChevronLeft className="w-5 h-5 text-slate-600" />}
                            </button>
                        </div>

                        {!isSidebarCollapsed && (
                            <div className="flex-1 overflow-y-auto min-h-0 pb-32">
                                <div className="p-2 md:p-4 space-y-3">

                                    {(() => {
                                        const lessonGroups: { [key: string]: { lessonName: string; questions: Array<{ question: ErrorBookletQuestion; globalIndex: number }> } } = {};

                                        questions.forEach((question, index) => {
                                            const lessonId = question.lessonId || 'other';
                                            const lessonName = question.lessonName || 'Diğer';

                                            if (!lessonGroups[lessonId]) {
                                                lessonGroups[lessonId] = {
                                                    lessonName,
                                                    questions: [],
                                                };
                                            }
                                            lessonGroups[lessonId].questions.push({ question, globalIndex: index });
                                        });

                                        return Object.entries(lessonGroups).map(([lessonId, group]) => (
                                            <div key={lessonId} className="space-y-1.5">
                                                <div className="text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-1 rounded-md">
                                                    {group.lessonName}
                                                    <span className="ml-1 text-slate-400">({group.questions.length})</span>
                                                </div>
                                                <div className="grid grid-cols-4 md:grid-cols-3 gap-1 md:gap-1.5">
                                                    {group.questions.map(({ question, globalIndex }) => (
                                                        <div
                                                            key={question.id}
                                                            className={getQuestionCardClass(globalIndex)}
                                                            onClick={() => goToQuestion(globalIndex)}
                                                        >
                                                            <div className="flex items-center justify-center">
                                                                <span className="font-medium text-slate-900 text-xs">{globalIndex + 1}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ));
                                    })()}
                                </div>

                                <div className="border-b border-slate-200"></div>

                                <div className="p-3 space-y-2">
                                    {!isCompleted && (
                                        <button
                                            onClick={handleSubmit}
                                            disabled={submitting}
                                            className="w-full p-2 rounded-lg text-sm cursor-pointer transition-all bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                            Testi Bitir
                                        </button>
                                    )}

                                    {isCompleted && (
                                        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3 shadow-sm">
                                            <div className="text-center">
                                                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center justify-center gap-2">
                                                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                                                    Test Sonucu
                                                </h4>

                                                <div className="mb-4">
                                                    <div className="relative mb-4">
                                                        <div className="w-20 h-20 mx-auto rounded-full bg-white flex items-center justify-center">
                                                            <svg className="w-20 h-20 absolute transform -rotate-90" viewBox="0 0 100 100">
                                                                <circle cx="50" cy="50" r="40" stroke="#e5e7eb" strokeWidth="8" fill="white" />
                                                                <circle
                                                                    cx="50"
                                                                    cy="50"
                                                                    r="40"
                                                                    stroke={stats.successRate >= 80 ? "#10b981" : stats.successRate >= 60 ? "#f59e0b" : "#ef4444"}
                                                                    strokeWidth="8"
                                                                    fill="none"
                                                                    strokeLinecap="round"
                                                                    strokeDasharray={`${2 * Math.PI * 40}`}
                                                                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - stats.successRate / 100)}`}
                                                                    className="transition-all duration-1000 ease-out"
                                                                />
                                                            </svg>
                                                            <div className="relative z-10 text-center">
                                                                <div className={`text-sm font-bold ${stats.successRate >= 80 ? "text-green-600" : stats.successRate >= 60 ? "text-yellow-600" : "text-red-600"
                                                                    }`}>
                                                                    %{stats.successRate}
                                                                </div>
                                                                <div className="text-xs text-gray-500 -mt-0.5">başarı</div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-2">
                                                        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-2">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                                                                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                                                                </div>
                                                                <span className="text-xs font-medium text-green-700">Doğru</span>
                                                            </div>
                                                            <div className="text-sm font-bold text-green-800">{stats.correctAnswers}</div>
                                                        </div>

                                                        <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-2">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                                                                    <X className="w-3 h-3 text-red-600" />
                                                                </div>
                                                                <span className="text-xs font-medium text-red-700">Yanlış</span>
                                                            </div>
                                                            <div className="text-sm font-bold text-red-800">{stats.wrongAnswers}</div>
                                                        </div>

                                                        <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-2">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                                                                    <Circle className="w-3 h-3 text-gray-500" />
                                                                </div>
                                                                <span className="text-xs font-medium text-gray-700">Boş</span>
                                                            </div>
                                                            <div className="text-sm font-bold text-gray-800">{stats.unanswered}</div>
                                                        </div>

                                                        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-2 mt-2">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                                                                    <ClipboardCheck className="w-3 h-3 text-blue-600" />
                                                                </div>
                                                                <span className="text-xs font-medium text-blue-700">Toplam</span>
                                                            </div>
                                                            <div className="text-sm font-bold text-blue-800">{stats.totalQuestions}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 flex flex-col min-h-0 bg-gray-50">
                        {currentQuestion && (
                            <>
                                <div className="flex-shrink-0 bg-white border-b border-slate-200 h-16 px-3 sm:px-4 flex items-center">
                                    <div className="w-full flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h2 className={`font-semibold text-slate-900 ${isFullscreen ? 'text-xl' : 'text-lg'}`}>
                                                Soru {currentQuestionIndex + 1} / {questions.length}
                                            </h2>
                                            {currentQuestion.lessonName && (
                                                <span className="px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                                                    {currentQuestion.lessonName}
                                                </span>
                                            )}
                                            {isCompleted && (
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${getAnswerStatus(currentQuestion.id) === 'correct'
                                                    ? 'bg-emerald-100 text-emerald-700'
                                                    : getAnswerStatus(currentQuestion.id) === 'incorrect'
                                                        ? 'bg-rose-100 text-rose-700'
                                                        : 'bg-amber-100 text-amber-700'
                                                    }`}>
                                                    {getAnswerStatus(currentQuestion.id) === 'correct' ? 'Doğru' : getAnswerStatus(currentQuestion.id) === 'incorrect' ? 'Yanlış' : 'Boş'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {isCompleted && currentQuestion.originalQuestion?.solutionMedia?.url && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedQuestionForSolution(currentQuestion.originalQuestion);
                                                        setIsSolutionVideoModalOpen(true);
                                                    }}
                                                    className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                                                >
                                                    <Play className="w-4 h-4" />
                                                    <span className="hidden sm:inline">Çözüm Videosu</span>
                                                </button>
                                            )}
                                            <button
                                                onClick={handleExpand}
                                                className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
                                                title={isFullscreen ? 'Küçült' : 'Tam Ekran'}
                                            >
                                                {isFullscreen ? <Shrink className="w-4 h-4" /> : <Expand className="w-4 h-4" />}
                                                <span className="hidden sm:inline">{isFullscreen ? 'Küçült' : 'Tam Ekran'}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className={`overflow-y-auto ${isFullscreen ? 'p-6 md:p-8' : 'p-4 md:p-6'}`}
                                    style={{
                                        touchAction: 'pan-y pinch-zoom',
                                        height: isFullscreen ? 'calc(100vh - 150px)' : 'calc(100vh - 325px)',
                                        maxHeight: isFullscreen ? 'calc(100vh - 150px)' : 'calc(100vh - 325px)'
                                    }}
                                >
                                    <div className="max-w-4xl mx-auto">
                                        {currentQuestion.originalQuestion?.media && (
                                            <div
                                                ref={imageContainerRef}
                                                className="mb-6 w-full flex justify-center items-center"
                                            >
                                                <img
                                                    src={currentQuestion.originalQuestion.media.url}
                                                    alt="Soru görseli"
                                                    className="max-w-full w-auto h-auto object-contain transition-transform duration-200 select-none rounded-lg shadow-sm"
                                                    style={{
                                                        transform: `scale(${imageZoom})`,
                                                        cursor: imageZoom > 1 ? "zoom-out" : "zoom-in",
                                                        maxHeight: isFullscreen ? 'calc(100vh - 300px)' : '60vh',
                                                    }}
                                                    onClick={handleImageClick}
                                                    draggable={false}
                                                />
                                            </div>
                                        )}

                                        {currentQuestion.originalQuestion?.content && (
                                            <div className="mb-6 bg-gray-50 rounded-lg border p-4">
                                                <h3 className="font-semibold text-gray-900 mb-2 text-lg">
                                                    Soru {currentQuestionIndex + 1}
                                                </h3>
                                                <div
                                                    className="text-gray-800 leading-relaxed prose prose-sm max-w-none"
                                                    dangerouslySetInnerHTML={{ __html: currentQuestion.originalQuestion.content }}
                                                />
                                            </div>
                                        )}

                                        {!currentQuestion.originalQuestion?.content && !currentQuestion.originalQuestion?.media && (
                                            <div className="mb-6 bg-gray-50 rounded-lg border p-4">
                                                <h3 className="font-semibold text-gray-900 mb-2 text-lg">
                                                    Soru {currentQuestionIndex + 1}
                                                </h3>
                                            </div>
                                        )}

                                        <div className="space-y-3 relative z-20">
                                            <div className="grid grid-cols-5 gap-1 sm:gap-2 max-w-5xl mx-auto">
                                                {currentQuestion.originalQuestion?.options?.map((option: any, optionIndex: number) => {
                                                    const optionValue = typeof option === 'object' ? option.value : option;
                                                    const isSelected = JSON.stringify(answers[currentQuestion.id]) === JSON.stringify(optionValue);
                                                    const correctOption = currentQuestion.originalQuestion?.correctOption;
                                                    const isCorrectOption = JSON.stringify(optionValue) === JSON.stringify(correctOption);
                                                    const answerStatus = getAnswerStatus(currentQuestion.id);

                                                    return (
                                                        <button
                                                            key={optionIndex}
                                                            onClick={() => handleAnswerSelect(currentQuestion.id, optionValue)}
                                                            disabled={isCompleted}
                                                            className={`w-full px-1 py-1 sm:p-4 text-center text-xs sm:text-base rounded border-2 transition-all hover:shadow-md relative z-30 ${isSelected
                                                                ? !isCompleted
                                                                    ? "border-indigo-500 bg-indigo-50 text-indigo-800"
                                                                    : answerStatus === "correct"
                                                                        ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                                                                        : "border-rose-500 bg-rose-50 text-rose-800"
                                                                : isCompleted && isCorrectOption
                                                                    ? "border-emerald-400 bg-emerald-25 text-emerald-700"
                                                                    : "border-slate-200 bg-white hover:border-slate-300 text-slate-800"
                                                                } ${isCompleted ? "cursor-not-allowed" : "cursor-pointer"}`}
                                                        >
                                                            <div className="flex items-center">
                                                                <span className="flex-1 text-center text-xs sm:text-base">{optionValue}</span>
                                                                {isSelected && (
                                                                    <CheckCircle2
                                                                        className={`w-3 h-3 sm:w-5 sm:h-5 mt-0 sm:mt-1 ${!isCompleted
                                                                            ? "text-indigo-500"
                                                                            : answerStatus === "correct"
                                                                                ? "text-emerald-500"
                                                                                : "text-rose-500"
                                                                            }`}
                                                                    />
                                                                )}
                                                                {isCompleted && isCorrectOption && !isSelected && (
                                                                    <CheckCircle2 className="w-3 h-3 sm:w-5 sm:h-5 mt-0 sm:mt-1 text-emerald-500" />
                                                                )}
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {isCompleted && (
                                            <div className="mt-6 p-4 rounded-lg border border-slate-200 bg-slate-50">
                                                <h4 className="font-semibold text-slate-900 mb-3">Cevap Detayları</h4>
                                                <div className="space-y-2 text-sm">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-slate-600">Doğru Cevap:</span>
                                                        <span className="font-medium text-emerald-600">
                                                            {currentQuestion.originalQuestion?.correctOption}
                                                        </span>
                                                    </div>
                                                    {answers[currentQuestion.id] ? (
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-slate-600">Sizin Cevabınız:</span>
                                                            <span className={`font-medium ${getAnswerStatus(currentQuestion.id) === "correct" ? "text-emerald-600" : "text-rose-600"
                                                                }`}>
                                                                {answers[currentQuestion.id]}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-slate-600">Sizin Cevabınız:</span>
                                                            <span className="font-medium text-amber-600">Cevaplanmamış</span>
                                                        </div>
                                                    )}
                                                    {currentQuestion.previousWrongAnswer && (
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-slate-600">Önceki Hatalı Cevabınız:</span>
                                                            <span className="font-medium text-orange-600">
                                                                {currentQuestion.previousWrongAnswer}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {currentQuestion.gain?.gain && (
                                                        <div>
                                                            <span className="text-slate-600">Kazanım:</span>
                                                            <p className="mt-1 text-slate-800 bg-white rounded p-2 border border-slate-200">
                                                                {currentQuestion.gain.gain}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {currentQuestion.previousWrongAnswer && !isCompleted && (
                                            <div className="mt-4 p-3 rounded-lg border border-amber-200 bg-amber-50">
                                                <div className="flex items-center gap-2 text-sm text-amber-800">
                                                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                                    <span>Bu soruyu daha önce yanlış cevaplamıştın. Şimdi doğru cevabı bulmaya çalış!</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0 p-3 bg-white border-t border-slate-200">
                            <button
                                onClick={goToPreviousQuestion}
                                disabled={currentQuestionIndex === 0}
                                className="flex items-center gap-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto justify-center px-4 py-2 sm:px-6 sm:py-3"
                            >
                                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                                <span className="text-sm sm:text-base">Önceki Soru</span>
                            </button>

                            <div className="text-slate-500 order-first sm:order-none text-sm">
                                {currentQuestionIndex + 1} / {questions.length}
                            </div>

                            {currentQuestionIndex === questions.length - 1 ? (
                                !isCompleted ? (
                                    <button
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                        className="flex items-center gap-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors w-full sm:w-auto justify-center px-4 py-2 sm:px-6 sm:py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                        <span className="text-sm sm:text-base">Testi Bitir</span>
                                        <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>
                                ) : null
                            ) : (
                                <button
                                    onClick={goToNextQuestion}
                                    className="flex items-center gap-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors w-full sm:w-auto justify-center px-4 py-2 sm:px-6 sm:py-3"
                                >
                                    <span className="text-sm sm:text-base">Sonraki Soru</span>
                                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <SolutionVideoModal
                isOpen={isSolutionVideoModalOpen}
                onClose={() => {
                    setIsSolutionVideoModalOpen(false);
                    setSelectedQuestionForSolution(null);
                }}
                question={selectedQuestionForSolution}
                questionIndex={currentQuestionIndex}
            />
        </>
    );
}